import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLeaf,
  faHourglassHalf,
  faRulerCombined,
  faScaleBalanced,
  faMountain,
  faTruck,
  faGasPump,
  faRecycle,
  faPhone,
} from '@fortawesome/free-solid-svg-icons';
import Header from '../components/Header';
import SEOHead from '../components/SEOHead';
import CTAButton from '../components/CTAButton';
import EmailAndPhoneButton from '../components/EmailAndPhoneButton';
import heroImage from '../images/Fundament.jpg';
import sideImage from '../images/jim-jbc.webp';
import './styles/LCA.css';

const fakta = [
  {
    icon: faHourglassHalf,
    titel: '50 år',
    tekst: 'Beregningen dækker bygningens klimapåvirkning over en 50-årig levetid – fra materialer til drift og bortskaffelse.',
  },
  {
    icon: faRulerCombined,
    titel: 'kg CO₂e/m²/år',
    tekst: 'Resultatet opgøres pr. kvadratmeter pr. år, så bygninger af forskellig størrelse kan sammenlignes.',
  },
  {
    icon: faScaleBalanced,
    titel: 'EN 15978',
    tekst: 'Metoden følger den europæiske standard for livscyklusvurdering af bygninger.',
  },
];

const graensevaerdier = [
  { aar: '2025', bolig: '6,7', proces: '1,5', aktiv: true },
  { aar: '2027', bolig: '6,0', proces: '1,3' },
  { aar: '2029', bolig: '5,4', proces: '1,1' },
];

const indsatser = [
  {
    icon: faMountain,
    titel: 'Jordhåndtering på grunden',
    tekst: 'Jord der kan genindbygges, bliver på grunden. Hver læssevogn der ikke skal køre til depot, er sparet CO₂.',
  },
  {
    icon: faTruck,
    titel: 'Planlagt transport',
    tekst: 'Færre og fyldte kørsler med materialer og jord gennem god logistik og lokale leverandører.',
  },
  {
    icon: faGasPump,
    titel: 'Maskiner og brændstof',
    tekst: 'Den rette maskine til opgaven og et effektivt maskinforbrug holder energiforbruget på pladsen nede.',
  },
  {
    icon: faRecycle,
    titel: 'Mindre materialespild',
    tekst: 'Præcis udførelse og sortering på pladsen betyder mindre spild – og spild tæller med i regnskabet.',
  },
];

const proces = [
  { nr: '01', titel: 'Tidlig dialog', tekst: 'Vi tager snakken med dig og din rådgiver, mens jordhåndtering og fundering stadig kan påvirkes.' },
  { nr: '02', titel: 'Planlægning', tekst: 'Vi lægger en plan for jord, transport og maskiner med fokus på at holde byggepladsens aftryk nede.' },
  { nr: '03', titel: 'Udførelse', tekst: 'Arbejdet udføres efter planen – ordentligt og effektivt, uden unødig kørsel og spild.' },
  { nr: '04', titel: 'Dokumentation', tekst: 'Vi registrerer forbrug af brændstof, transport og spild, så tallene kan indgå i projektets LCA.' },
];

const faq = [
  {
    q: 'Skal mit enfamiliehus have en LCA?',
    a: 'Ja. For byggetilladelser søgt fra 1. juli 2025 gælder klimakravene i bygningsreglementet for alt nybyggeri, også enfamiliehuse, rækkehuse og sommerhuse. Grænseværdien for enfamiliehuse er 6,7 kg CO₂e pr. m² pr. år.',
  },
  {
    q: 'Hvad er grænseværdien for byggeprocessen?',
    a: 'Byggeprocessen har sin egen grænseværdi på 1,5 kg CO₂e pr. m² pr. år. Den dækker transport til byggepladsen, energi- og brændstofforbrug på pladsen og materialespild.',
  },
  {
    q: 'Hvornår bliver kravene strammet?',
    a: 'Grænseværdierne revideres i 2027 og 2029. For enfamiliehuse ventes de at falde til 6,0 i 2027 og 5,4 i 2029, og for byggeprocessen til 1,3 og 1,1 kg CO₂e pr. m² pr. år.',
  },
  {
    q: 'Hvem laver selve LCA-beregningen?',
    a: 'Beregningen udarbejdes typisk af projektets rådgiver, fx arkitekt eller ingeniør. Som entreprenør bidrager vi med planlægning af jord, transport og maskiner – og med data om forbrug fra byggepladsen.',
  },
  {
    q: 'Gælder kravene også for tilbygninger?',
    a: 'For enfamiliehuse, rækkehuse og sommerhuse gælder grænseværdien først for tilbygninger over 250 m². For etageboliger, kontorer og institutioner gælder den også ved mindre tilbygninger.',
  },
];

function LCA() {
  const navigate = useNavigate();

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Bæredygtigt anlægsarbejde og LCA',
    description: 'Anlægs- og entreprenørarbejde med fokus på klimakravene i bygningsreglementet: jordhåndtering, transport, maskinforbrug og dokumentation til LCA.',
    provider: {
      '@type': 'Organization',
      name: 'JBC Entreprenøren',
      url: 'https://jbcentreprenoeren.dk',
    },
    areaServed: { '@type': 'Place', name: 'Sjælland' },
  };

  return (
    <div className="lca-page">
      <SEOHead
        title="LCA og klimakrav i byggeriet | JBC Entreprenøren"
        description="Siden 1. juli 2025 gælder klimakravene alt nybyggeri – også byggepladsen. Se grænseværdierne, og hvordan vi holder CO₂-aftrykket nede på dit projekt."
        url="/lca"
        image="https://jbcentreprenoeren.dk/images/og-lca.jpg"
        structuredData={structuredData}
        faq={faq}
        breadcrumbs={[{ name: 'Hjem', url: '/' }, { name: 'LCA & Bæredygtighed', url: '/lca' }]}
      />
      <Header />
      <EmailAndPhoneButton />

      <section className="lca-hero">
        <div className="lca-hero-bg">
          <img src={heroImage} alt="" />
        </div>
        <div className="lca-hero-inner">
          <span className="lca-eyebrow">
            <FontAwesomeIcon icon={faLeaf} /> Bæredygtighed &amp; LCA
          </span>
          <h1>Klimaaftrykket starter på byggepladsen</h1>
          <p>
            Siden 1. juli 2025 skal alt nybyggeri overholde grænseværdier for CO₂ – også selve byggeprocessen.
            Vi hjælper dig med at holde aftrykket nede fra første spadestik.
          </p>
          <div className="lca-hero-actions">
            <CTAButton text="Tal med os om dit projekt" onClick={() => navigate('/kontakt')} />
            <a href="#krav" className="lca-hero-link">Se kravene ↓</a>
          </div>
        </div>
      </section>

      <section className="lca-section">
        <div className="lca-intro">
          <span className="lca-eyebrow">Hvad er LCA?</span>
          <h2>En livscyklusvurdering i korte træk</h2>
          <p>
            LCA står for livscyklusvurdering. Den beregner, hvor meget CO₂ en bygning er skyld i gennem hele sit liv –
            fra udvinding og produktion af materialer, over transport og byggeplads, til drift, vedligehold og nedrivning.
          </p>
        </div>
        <div className="lca-fakta-grid">
          {fakta.map((f) => (
            <div className="lca-fakta" key={f.titel}>
              <div className="lca-ikon">
                <FontAwesomeIcon icon={f.icon} />
              </div>
              <h3>{f.titel}</h3>
              <p>{f.tekst}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="lca-section lca-section-tint" id="krav">
        <div className="lca-krav">
          <div className="lca-krav-tekst">
            <span className="lca-eyebrow">Kravene i dag</span>
            <h2>Grænseværdierne strammes hvert andet år</h2>
            <p>
              Klimakravene står i bygningsreglementet (BR18 §§ 297–298). Fra 1. juli 2025 gælder de for alt nybyggeri,
              inklusive enfamiliehuse og rækkehuse. Samtidig er der kommet en selvstændig grænseværdi for byggeprocessen –
              transport til pladsen, energi og brændstof på pladsen samt materialespild.
            </p>
            <p className="lca-krav-note">
              Det er netop byggeprocessen, entreprenøren har direkte indflydelse på.
            </p>
          </div>
          <div className="lca-tabel-wrap">
            <table className="lca-tabel">
              <thead>
                <tr>
                  <th>Fra</th>
                  <th>Enfamiliehuse</th>
                  <th>Byggeprocessen</th>
                </tr>
              </thead>
              <tbody>
                {graensevaerdier.map((g) => (
                  <tr key={g.aar} className={g.aktiv ? 'aktiv' : ''}>
                    <td>
                      {g.aar}
                      {g.aktiv && <span className="lca-nu">Gælder nu</span>}
                    </td>
                    <td><strong>{g.bolig}</strong></td>
                    <td><strong>{g.proces}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="lca-tabel-kilde">
              Alle tal i kg CO₂e pr. m² pr. år. Værdierne for 2027 og 2029 er de forventede stramninger.
              Kilde: Social- og Boligstyrelsen / bygningsreglementet.
            </p>
          </div>
        </div>
      </section>

      <section className="lca-section">
        <div className="lca-split">
          <div className="lca-split-billede">
            <img src={sideImage} alt="JBC Entreprenøren i gang med gravearbejde" loading="lazy" decoding="async" />
          </div>
          <div className="lca-split-indhold">
            <span className="lca-eyebrow">Hvor vi gør forskellen</span>
            <h2>Fire steder, hvor CO₂ spares i praksis</h2>
            <div className="lca-indsatser">
              {indsatser.map((i) => (
                <div className="lca-indsats" key={i.titel}>
                  <div className="lca-ikon lca-ikon-sm">
                    <FontAwesomeIcon icon={i.icon} />
                  </div>
                  <div>
                    <h3>{i.titel}</h3>
                    <p>{i.tekst}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="lca-section lca-section-tint">
        <div className="lca-intro">
          <span className="lca-eyebrow">Sådan arbejder vi</span>
          <h2>Fra første møde til dokumentation</h2>
        </div>
        <div className="lca-proces">
          {proces.map((p) => (
            <div className="lca-trin" key={p.nr}>
              <span className="lca-trin-nr">{p.nr}</span>
              <h3>{p.titel}</h3>
              <p>{p.tekst}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="lca-section">
        <div className="lca-intro">
          <span className="lca-eyebrow">Ofte stillede spørgsmål</span>
          <h2>Spørgsmål om LCA og klimakrav</h2>
        </div>
        <div className="lca-faq">
          {faq.map((item) => (
            <details className="lca-faq-item" key={item.q}>
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="lca-cta">
        <div className="lca-cta-inner">
          <h2>Har du et projekt med klimakrav?</h2>
          <p>Kontakt os tidligt – så er der mest at hente på jord, transport og logistik.</p>
          <div className="lca-cta-actions">
            <CTAButton text="Få et uforpligtende tilbud" onClick={() => navigate('/kontakt')} />
            <a href="tel:+4522780476" className="lca-cta-phone">
              <FontAwesomeIcon icon={faPhone} /> 22 78 04 76
            </a>
          </div>
          <Link to="/" className="lca-cta-back">← Tilbage til forsiden</Link>
        </div>
      </section>
    </div>
  );
}

export default LCA;
